import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { getRepo, META_CURRENT_GAME, META_CURRENT_SEASON, type Repo } from '../data'
import { readCachedSnapshot, refreshSnapshot } from '../data/snapshotClient'
import type { CachedSnapshot } from '../data/snapshotTypes'
import { reconcilePlayers } from './reconcile'
import { adoptServerGameIds } from './reconcileGames'
import { loadGuestData } from './guestData'
import { discardGame, purgeStaleDrafts } from './drafts'
import { defaultEnabledStatTypes } from '../domain/statTypes'
import { todayISO } from '../lib/date'
import { newId } from '../lib/id'
import type { Game, ID, Opponent, Player, Season } from '../domain/types'

interface AppState {
  repo: Repo
  players: Player[]
  opponents: Opponent[]
  seasons: Season[]
  games: Game[]
  currentSeason: Season
  /** The in-progress game, if one was left open when the app was last closed. */
  currentGameId: ID | null

  /** Cached PlayHQ snapshot, or null before the first successful fetch. */
  snapshot: CachedSnapshot | null
  /** Set when the last refresh failed. The cached copy is still usable. */
  snapshotError: string | null
  snapshotRefreshing: boolean
  /** Pull-to-refresh / manual refresh. Never throws; reports through state. */
  refreshFromServer(): Promise<void>

  addPlayer(name: string): Promise<Player>
  updatePlayer(p: Player): Promise<void>
  addOpponent(name: string): Promise<Opponent>
  updateSeason(s: Season): Promise<void>
  setCurrentSeason(id: ID): Promise<void>
  saveGame(g: Game): Promise<void>
  /** Removes the game and its events. Refuses a completed game. */
  deleteGame(g: Game): Promise<void>
  setCurrentGameId(id: ID | null): Promise<void>
  reload(): Promise<void>
}

const Ctx = createContext<AppState | null>(null)

/**
 * Bootstrap runs exactly once per page load. React StrictMode invokes effects
 * twice in development, and two concurrent boots each wrote a full set of
 * players -- which is how a squad of 7 became 14 rows on the iPad.
 */
let bootstrap: Promise<Repo> | null = null

export function useApp(): AppState {
  const value = useContext(Ctx)
  if (!value) throw new Error('useApp must be used inside <AppStoreProvider>')
  return value
}

function makeSeason(): Season {
  const now = Date.now()
  const year = new Date().getFullYear()
  return {
    id: newId(),
    name: `${year} Season`,
    startDate: todayISO(),
    endDate: null,
    periodFormat: 'halves',
    enabledStatTypes: defaultEnabledStatTypes(),
    createdAt: now,
    updatedAt: now,
  }
}

export function AppStoreProvider({ children, guest = false }: { children: ReactNode; guest?: boolean }) {
  const [repo, setRepo] = useState<Repo | null>(null)
  const [players, setPlayers] = useState<Player[]>([])
  const [opponents, setOpponents] = useState<Opponent[]>([])
  const [seasons, setSeasons] = useState<Season[]>([])
  const [games, setGames] = useState<Game[]>([])
  const [currentSeasonId, setCurrentSeasonId] = useState<ID | null>(null)
  const [currentGameId, setCurrentGameIdState] = useState<ID | null>(null)
  const [snapshot, setSnapshot] = useState<CachedSnapshot | null>(null)
  const [snapshotError, setSnapshotError] = useState<string | null>(null)
  const [snapshotRefreshing, setSnapshotRefreshing] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const load = useCallback(async (r: Repo) => {
    const [p, o, s, g, gameId, seasonId] = await Promise.all([
      r.listPlayers(),
      r.listOpponents(),
      r.listSeasons(),
      r.listGames(),
      r.getMeta<ID>(META_CURRENT_GAME),
      r.getMeta<ID>(META_CURRENT_SEASON),
    ])

    let allSeasons = s
    let activeSeasonId = seasonId ?? null
    // First run: create a season so the user can go straight to a game.
    if (allSeasons.length === 0) {
      const season = makeSeason()
      await r.saveSeason(season)
      await r.setMeta(META_CURRENT_SEASON, season.id)
      allSeasons = [season]
      activeSeasonId = season.id
    } else if (!activeSeasonId || !allSeasons.some((x) => x.id === activeSeasonId)) {
      activeSeasonId = allSeasons[allSeasons.length - 1]!.id
      await r.setMeta(META_CURRENT_SEASON, activeSeasonId)
    }

    // Only resume a game that is genuinely still open.
    const openGame = gameId ? g.find((x) => x.id === gameId && x.status === 'in_progress') : undefined

    // Drafts nobody came back to are removed on launch (02.7).
    const purged = await purgeStaleDrafts(r, g, openGame?.id ?? null)
    const remainingGames = purged.length ? g.filter((x) => !purged.includes(x.id)) : g

    // The cached snapshot is part of boot: the app must be fully usable offline
    // before any network call is attempted.
    const cached = await readCachedSnapshot(r)
    let bootPlayers = p
    let bootGames = remainingGames
    let bootCurrentGame = openGame?.id ?? null
    if (cached) {
      const reconciled = await reconcilePlayers(r, p, cached.snapshot.roster)
      bootPlayers = reconciled.players
      // Games move onto the server's id for the same reason players did: two
      // devices scoring one fixture have to be scoring the same game.
      const rekeyed = await adoptServerGameIds(r, bootGames, cached.snapshot.games, bootCurrentGame)
      bootGames = rekeyed.games
      if (rekeyed.currentGameId !== bootCurrentGame) {
        bootCurrentGame = rekeyed.currentGameId
        await r.setMeta(META_CURRENT_GAME, bootCurrentGame)
      }
      setSnapshot(cached)
    }

    setPlayers(bootPlayers)
    setOpponents(o)
    setSeasons(allSeasons)
    setGames(bootGames)
    setCurrentSeasonId(activeSeasonId)
    setCurrentGameIdState(bootCurrentGame)
  }, [])

  /**
   * Fetch the snapshot and fold its roster into local players. Never throws --
   * a failure leaves the cached copy in place and surfaces through snapshotError.
   */
  const doRefresh = useCallback(async (r: Repo) => {
    setSnapshotRefreshing(true)
    try {
      const result = await refreshSnapshot(r)
      setSnapshotError(result.error)
      if (result.cached) {
        setSnapshot(result.cached)
        // Read the current players from storage rather than from state: a state
        // updater must stay pure, and storage is the authority here anyway.
        const existing = await r.listPlayers()
        const out = await reconcilePlayers(r, existing, result.cached.snapshot.roster)
        setPlayers(out.players)

        const localGames = await r.listGames()
        const current = (await r.getMeta<ID>(META_CURRENT_GAME)) ?? null
        const rekeyed = await adoptServerGameIds(r, localGames, result.cached.snapshot.games, current)
        if (rekeyed.rekeyed > 0) {
          setGames(rekeyed.games)
          if (rekeyed.currentGameId !== current) {
            await r.setMeta(META_CURRENT_GAME, rekeyed.currentGameId)
            setCurrentGameIdState(rekeyed.currentGameId)
          }
        }
      }
    } finally {
      setSnapshotRefreshing(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!bootstrap) bootstrap = getRepo()
    bootstrap
      .then(async (r) => {
        if (cancelled) return
        await load(r)
        if (cancelled) return
        // Staging guest: pull the demo season into the local store BEFORE the
        // app renders, so the read-only Stats/live screens have data on first
        // paint. Guarded so a real user's store is never touched.
        if (guest) {
          try {
            const result = await refreshSnapshot(r)
            if (result.cached) {
              // load(r) has just ensured a current season and stored its id.
              const sid = await r.getMeta<ID>(META_CURRENT_SEASON)
              if (sid) await loadGuestData(r, result.cached.snapshot, sid)
              setSnapshot(result.cached)
            }
            await load(r)
          } catch {
            /* a guest with no network just sees empty screens; no crash */
          }
        }
        if (cancelled) return
        setRepo(r)
        // Non-blocking: the app is already usable from cache, so a slow or
        // absent network must never delay the setup screen.
        if (!guest) void doRefresh(r)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)))
      })
    return () => {
      cancelled = true
    }
  }, [load])

  const value = useMemo<AppState | null>(() => {
    if (!repo || !currentSeasonId) return null
    const currentSeason = seasons.find((s) => s.id === currentSeasonId) ?? seasons[0]!

    const upsert = <T extends { id: ID }>(rows: T[], row: T): T[] => {
      const i = rows.findIndex((r) => r.id === row.id)
      if (i < 0) return [...rows, row]
      const next = rows.slice()
      next[i] = row
      return next
    }

    return {
      repo,
      players,
      opponents,
      seasons,
      games,
      currentSeason,
      currentGameId,
      snapshot,
      snapshotError,
      snapshotRefreshing,

      async refreshFromServer() {
        await doRefresh(repo)
      },

      async addPlayer(name) {
        const now = Date.now()
        const player: Player = { id: newId(), name: name.trim(), active: true, createdAt: now, updatedAt: now }
        await repo.savePlayer(player)
        setPlayers((prev) => [...prev, player])
        return player
      },
      async updatePlayer(p) {
        const next = { ...p, updatedAt: Date.now() }
        await repo.savePlayer(next)
        setPlayers((prev) => upsert(prev, next))
      },
      async addOpponent(name) {
        const now = Date.now()
        const opponent: Opponent = { id: newId(), name: name.trim(), createdAt: now, updatedAt: now }
        await repo.saveOpponent(opponent)
        setOpponents((prev) => [...prev, opponent])
        return opponent
      },
      async updateSeason(s) {
        const next = { ...s, updatedAt: Date.now() }
        await repo.saveSeason(next)
        setSeasons((prev) => upsert(prev, next))
      },
      async setCurrentSeason(id) {
        await repo.setMeta(META_CURRENT_SEASON, id)
        setCurrentSeasonId(id)
      },
      async saveGame(g) {
        const next = { ...g, updatedAt: Date.now() }
        await repo.saveGame(next)
        setGames((prev) => upsert(prev, next))
      },
      async deleteGame(g) {
        await discardGame(repo, g)
        setGames((prev) => prev.filter((x) => x.id !== g.id))
      },
      async setCurrentGameId(id) {
        await repo.setMeta(META_CURRENT_GAME, id)
        setCurrentGameIdState(id)
      },
      async reload() {
        await load(repo)
      },
    }
  }, [
    repo,
    players,
    opponents,
    seasons,
    games,
    currentSeasonId,
    currentGameId,
    snapshot,
    snapshotError,
    snapshotRefreshing,
    doRefresh,
    load,
  ])

  if (error) {
    return (
      <div className="boot boot--error">
        <h1>Storage unavailable</h1>
        <p>{error.message}</p>
        <p className="muted">
          Neither IndexedDB nor localStorage could be opened. If Safari is in Private Browsing,
          close the private tab and open the app normally.
        </p>
      </div>
    )
  }

  if (!value) {
    return (
      <div className="boot">
        <div className="boot__spinner" />
      </div>
    )
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
